import { useMutation } from '@tanstack/react-query';
import { LaylatAlomrFormSchemaProps } from '@/utils/schema/laylat-alomr';

const BASE_URL = import.meta.env.VITE_BACKEND_URL;

type Payload = {
  file: File;
  companyName: string;
  registrationNumber: string;
  fileType: 'company_logo' | 'registration_document';
};

type UploaderResponse = {
  url: string;
  success?: boolean;
};

export function useLaylatAlomrFileUploader() {
  return useMutation({
    mutationFn: async (payload: Payload[]) => {
      const formDatas = payload.map((p) => {
        const formData = new FormData();
        formData.append('file', p.file);
        formData.append('companyName', p.companyName);
        formData.append('registrationNumber', p.registrationNumber);
        formData.append('fileType', p.fileType);
        return formData;
      });

      const responses = await Promise.all(
        formDatas.map((formData) =>
          fetch(`${BASE_URL}/upload`, {
            method: 'POST',
            body: formData,
          })
        )
      );

      return responses.map(async (response) => (await response.json()) as UploaderResponse);
    },
  });
}

export function useLaylatAlomrFormHandler(recaptchaKey: string) {
  return useMutation({
    mutationFn: async (data: LaylatAlomrFormSchemaProps) => {
      // Verify reCAPTCHA token
      const token = await window.grecaptcha.execute(recaptchaKey, {
        action: 'submit',
      });

      // Proceed with file uploads and form submission
      const payload = { ...data };

      const companyLogo = data?.companyLogo;
      const regDocument = data?.registrationDocument;
      const letterOfAuthorization = data?.letterOfAuthorization;

      const supportStartDate = new Date(payload.supportStartDate).toLocaleDateString('en-US');
      const supportEndDate = new Date(payload.supportEndDate).toLocaleDateString('en-US');
      const termsAccepted = payload.termsAccepted ? 'Yes' : 'No';

      const filteredSupportCategory = payload.supportCategory.filter((cat) => cat !== 'Both');

      const formData = new FormData();
      Object.entries({
        ...payload,
        supportStartDate,
        supportEndDate,
        termsAccepted,
        supportClassification: payload.supportClassification.join(', '),
        supportCategory: filteredSupportCategory.join(', '),
        expectedBeneficiaries: payload.expectedBeneficiaries,
        registrationDocumentFile: regDocument,
        companyLogoFile: companyLogo,
        letterOfAuthorizationFile: letterOfAuthorization,
        companyLogo: '',
        registrationDocument: '',
        letterOfAuthorization: '',
        token,
      }).forEach(([key, value]) => {
        if (typeof value === 'undefined') {
          formData.append(key, '');
        } else {
          formData.append(key, value);
        }
      });

      const response = await fetch(`${BASE_URL}/exec`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      return await response.json();
    },
    retry: 0,
  });
}
